import { useState, useRef, useCallback, useEffect } from 'react';
import { toast } from 'sonner';

/**
 * Custom hook for voice recognition with proper cleanup and text handling.
 * Fixes the duplication bug by tracking the last final transcript index
 * and only appending new final results.
 * 
 * @param {Object} options
 * @param {string} options.lang - Language code (default: 'it-IT')
 * @param {boolean} options.continuous - Continue listening (default: true)
 * @param {boolean} options.showToasts - Show toast notifications (default: true)
 * @returns {Object} - Voice recognition state and controls
 */
export function useVoiceRecognition(options = {}) {
  const {
    lang = 'it-IT',
    continuous = true,
    showToasts = true
  } = options;

  const [isListening, setIsListening] = useState(false);
  const [transcript, setTranscript] = useState('');
  const [interimTranscript, setInterimTranscript] = useState('');
  const [isSupported, setIsSupported] = useState(true);
  
  const recognitionRef = useRef(null);
  const finalTranscriptRef = useRef('');

  // Check browser support on mount
  useEffect(() => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) {
      setIsSupported(false);
    }
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (recognitionRef.current) {
        recognitionRef.current.abort();
        recognitionRef.current = null;
      }
    };
  }, []);

  const startListening = useCallback(() => {
    if (!isSupported) {
      if (showToasts) {
        toast.error('Il tuo browser non supporta il riconoscimento vocale. Usa Chrome o Edge.');
      }
      return false;
    }

    // Stop any existing recognition
    if (recognitionRef.current) {
      recognitionRef.current.abort();
    }

    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    recognitionRef.current = new SpeechRecognition();
    
    const recognition = recognitionRef.current;
    recognition.lang = lang;
    recognition.continuous = continuous;
    recognition.interimResults = true;

    // Reset transcripts
    finalTranscriptRef.current = '';
    setTranscript('');
    setInterimTranscript('');

    recognition.onstart = () => {
      setIsListening(true);
      if (showToasts) {
        toast.info('Sto ascoltando...');
      }
    };

    recognition.onresult = (event) => {
      let currentInterim = '';
      let newFinal = '';

      // Process all results
      for (let i = 0; i < event.results.length; i++) {
        const result = event.results[i];
        const resultTranscript = result[0].transcript;

        if (result.isFinal) {
          // Only add if this is a new final result
          // (not already in finalTranscriptRef)
          newFinal += resultTranscript;
        } else {
          currentInterim += resultTranscript;
        }
      }

      // Update final transcript - replace entirely with new finals
      // This prevents duplication because each onresult event gives us
      // ALL results from the beginning of this session
      if (newFinal) {
        finalTranscriptRef.current = newFinal;
        setTranscript(newFinal);
      }
      
      // Update interim (temporary, unconfirmed) transcript
      setInterimTranscript(currentInterim);
    };

    recognition.onerror = (event) => {
      console.error('Speech recognition error:', event.error);
      if (event.error !== 'no-speech' && event.error !== 'aborted') {
        if (showToasts) {
          toast.error('Errore nel riconoscimento vocale');
        }
      }
      setIsListening(false);
    };

    recognition.onend = () => {
      setIsListening(false);
      // Clear interim on end
      setInterimTranscript('');
    };

    try {
      recognition.start();
      return true;
    } catch (error) {
      console.error('Failed to start speech recognition:', error);
      setIsListening(false);
      return false;
    }
  }, [isSupported, lang, continuous, showToasts]);

  const stopListening = useCallback(() => {
    if (recognitionRef.current) {
      recognitionRef.current.stop();
    }
    setIsListening(false);
    setInterimTranscript('');
  }, []);

  const abortListening = useCallback(() => {
    if (recognitionRef.current) {
      recognitionRef.current.abort();
    }
    setIsListening(false);
    setInterimTranscript('');
  }, []);

  const resetTranscript = useCallback(() => {
    finalTranscriptRef.current = '';
    setTranscript('');
    setInterimTranscript('');
  }, []);

  const setTranscriptManually = useCallback((text) => {
    finalTranscriptRef.current = text;
    setTranscript(text);
  }, []);

  // Full text combines confirmed and interim
  const fullTranscript = transcript + (interimTranscript ? ' ' + interimTranscript : '');

  return {
    // State
    isListening,
    isSupported,
    transcript,           // Only final/confirmed text
    interimTranscript,    // Temporary text being processed
    fullTranscript,       // Combined text (final + interim)
    
    // Actions
    startListening,
    stopListening,
    abortListening,
    resetTranscript,
    setTranscript: setTranscriptManually
  };
}

export default useVoiceRecognition;
